/**
 * In-memory session cache for authenticated GETs shared across hook instances.
 * Dedupes in-flight requests, applies a short TTL, and clears on logout.
 * Data never leaves the tab; always fetched via the JWT-backed `api` client.
 */

export type AuthenticatedResourceKey =
  | 'plan-status'
  | 'whatsapp-connected-accounts'
  | 'payment-config'
  | 'payment-subscription'
  | 'payment-methods'
  | 'payment-billing-history'
  | 'international-pricing';

export const DEFAULT_SESSION_RESOURCE_TTL_MS = 60_000;

type CacheEntry<T> = {
  data: T;
  fetchedAt: number;
};

type Listener<T> = (data: T | undefined) => void;

const store = new Map<AuthenticatedResourceKey, CacheEntry<unknown>>();
const inflight = new Map<AuthenticatedResourceKey, Promise<unknown>>();
const listeners = new Map<
  AuthenticatedResourceKey,
  Set<Listener<unknown>>
>();

function notify<T>(key: AuthenticatedResourceKey, data: T | undefined): void {
  const set = listeners.get(key);
  if (!set) return;
  set.forEach((listener) => {
    try {
      (listener as Listener<T>)(data);
    } catch (err) {
      console.error(`[authenticatedResource] listener error for ${key}:`, err);
    }
  });
}

export function subscribeAuthenticatedResource<T>(
  key: AuthenticatedResourceKey,
  listener: Listener<T>
): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(listener as Listener<unknown>);
  return () => {
    set!.delete(listener as Listener<unknown>);
    if (set!.size === 0) listeners.delete(key);
  };
}

export function getCachedAuthenticatedResource<T>(
  key: AuthenticatedResourceKey
): T | undefined {
  const entry = store.get(key) as CacheEntry<T> | undefined;
  return entry?.data;
}

export function isAuthenticatedResourceFresh(
  key: AuthenticatedResourceKey,
  ttlMs: number = DEFAULT_SESSION_RESOURCE_TTL_MS
): boolean {
  const entry = store.get(key);
  if (!entry) return false;
  return Date.now() - entry.fetchedAt < ttlMs;
}

export function invalidateAuthenticatedResource(
  key: AuthenticatedResourceKey
): void {
  store.delete(key);
  // Keep inflight so concurrent waiters still resolve; next getOrFetch without
  // force will join inflight or start fresh after it clears.
  notify(key, undefined);
}

export function clearAuthenticatedResources(): void {
  const keys = new Set<AuthenticatedResourceKey>([
    ...Array.from(store.keys()),
    ...Array.from(listeners.keys()),
  ]);
  store.clear();
  inflight.clear();
  keys.forEach((key) => notify(key, undefined));
}

export async function getOrFetchAuthenticatedResource<T>(
  key: AuthenticatedResourceKey,
  fetcher: () => Promise<T>,
  options?: { force?: boolean; ttlMs?: number }
): Promise<T> {
  const ttlMs = options?.ttlMs ?? DEFAULT_SESSION_RESOURCE_TTL_MS;
  const force = options?.force ?? false;

  if (!force && isAuthenticatedResourceFresh(key, ttlMs)) {
    return getCachedAuthenticatedResource<T>(key) as T;
  }

  if (!force) {
    const existing = inflight.get(key) as Promise<T> | undefined;
    if (existing) return existing;
  }

  const promise = (async () => {
    const data = await fetcher();
    store.set(key, { data, fetchedAt: Date.now() });
    notify(key, data);
    return data;
  })();

  inflight.set(key, promise);

  try {
    return await promise;
  } catch (err) {
    store.delete(key);
    throw err;
  } finally {
    if (inflight.get(key) === promise) {
      inflight.delete(key);
    }
  }
}
